/**
 * Agent Test Dialog Component
 *
 * Interface for testing agent execution with custom tasks.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Chip
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import agentManagementService from '../../services/agentManagementService';

export default function AgentTestDialog({ open, agent, onClose }) {
  const [task, setTask] = useState('');
  const [context, setContext] = useState('');
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleExecute = async () => {
    if (!task.trim()) {
      setError('Please enter a task description');
      return;
    }

    try {
      setExecuting(true);
      setError(null);
      setResult(null);

      const contextObj = context.trim() ? JSON.parse(context) : null;

      const response = await agentManagementService.executeAgentTask({
        agent_id: agent.agent_id,
        task: task,
        context: contextObj,
        stream: false
      });

      setResult(response);
    } catch (err) {
      console.error('Error executing agent task:', err);
      if (context.trim() && err.message.includes('JSON')) {
        setError('Invalid JSON in context field');
      } else {
        setError(err.response?.data?.detail || 'Failed to execute task');
      }
    } finally {
      setExecuting(false);
    }
  };

  const handleClose = () => {
    setTask('');
    setContext('');
    setResult(null);
    setError(null);
    onClose();
  };

  const formatCost = (cost) => {
    return `$${cost.toFixed(6)}`;
  };

  if (!agent) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Test {agent.name}</Typography>
          <Button startIcon={<CloseIcon />} onClick={handleClose}>
            Close
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Task Input */}
          <TextField
            label="Task Description"
            multiline
            rows={4}
            value={task}
            onChange={(e) => setTask(e.target.value)}
            fullWidth
            placeholder="Enter a task for the agent to perform..."
            helperText="Describe what you want the agent to do"
          />

          {/* Context Input (Optional) */}
          <TextField
            label="Context (Optional)"
            multiline
            rows={3}
            value={context}
            onChange={(e) => setContext(e.target.value)}
            fullWidth
            placeholder='{"key": "value"}'
            helperText="Optional JSON context for the task"
          />

          {/* Execute Button */}
          <Button
            variant="contained"
            startIcon={executing ? <CircularProgress size={20} /> : <PlayArrowIcon />}
            onClick={handleExecute}
            disabled={executing || !task.trim()}
            fullWidth
            size="large"
          >
            {executing ? 'Executing...' : 'Execute Task'}
          </Button>

          {/* Result Display */}
          {result && (
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', mt: 2 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Execution Result
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Chip
                    label={result.success ? 'Success' : 'Failed'}
                    color={result.success ? 'success' : 'error'}
                    size="small"
                  />
                  {result.metrics && (
                    <>
                      <Chip
                        label={`${result.metrics.tokens_used} tokens`}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={formatCost(result.metrics.cost_usd)}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={`${result.metrics.duration_seconds.toFixed(2)}s`}
                        size="small"
                        variant="outlined"
                      />
                    </>
                  )}
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {result.success ? (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Output
                  </Typography>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: 'white',
                      maxHeight: 400,
                      overflow: 'auto',
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {result.output}
                  </Paper>
                </>
              ) : (
                <Alert severity="error">
                  <Typography variant="body2">
                    {result.error || 'Execution failed'}
                  </Typography>
                </Alert>
              )}

              {result.metadata && Object.keys(result.metadata).length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Metadata
                  </Typography>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: 'white',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem'
                    }}
                  >
                    <pre>{JSON.stringify(result.metadata, null, 2)}</pre>
                  </Paper>
                </>
              )}
            </Paper>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
